// src/pages/client/about/About.js
import React from "react";
import Container from "../../../components/Container";
import { Building2, Mail, /* Phone, */ MapPin, IdCard } from "lucide-react";
import { CheckCircle2 } from "lucide-react"; // ⬅ bullet 아이콘
import { useTranslation } from "react-i18next";

export default function AboutUs() {
  return (
    <div className="text-blue-900">
      <Greeting />
      <Overview />
      <Solutions /> {/* ⬅ 추가: 핵심 솔루션 */}
      <HowItWorks /> {/* ⬅ 추가: 운영 프로세스 */}
    </div>
  );
}

/* 1) Greeting */
function Greeting() {
  const { t } = useTranslation();
  const paras = t("about.greeting.paragraphs", { returnObjects: true });

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(120%_60%_at_0%_0%,#f1f5ff,transparent_60%),radial-gradient(120%_60%_at_100%_100%,#eef2ff,transparent_60%)]" />
      <Container className="grid gap-8 py-16 md:grid-cols-2 md:py-24">
        <h1 className="text-4xl font-extrabold tracking-tight md:text-6xl">
          {t("about.greeting.title")}
        </h1>

        <div className="space-y-4 leading-7 text-slate-700">
          {paras.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* 2) Overview (회사 기본 정보) */
function Overview() {
  const { t } = useTranslation();
  const f = t("about.overview", { returnObjects: true });

  const mapHref = f.addressFull
    ? `https://maps.google.com/?q=${encodeURIComponent(f.addressFull)}`
    : undefined;

  return (
    <section className="relative">
      <Container className="grid gap-10 py-16 md:grid-cols-[1fr,520px] md:py-24">
        {/* 왼쪽: 항목 리스트 */}
        <div>
          <h2 className="text-4xl font-extrabold tracking-tight md:text-6xl">
            {t("about.overview.title")}
          </h2>

          <dl className="mt-10 space-y-5 text-slate-700">
            <Item
              icon={Building2}
              label={t("about.overview.labels.company")}
              value={f.company}
            />
            <Item
              icon={IdCard}
              label={t("about.overview.labels.ceo")}
              value={f.ceo}
            />
            <Item
              icon={IdCard}
              label={t("about.overview.labels.regNo")}
              value={f.regNo}
            />
            {/* <Item
              icon={Building2}
              label={t("about.overview.labels.since")}
              value={f.since}
            /> */}
            <Item
              icon={Building2}
              label={t("about.overview.labels.business")}
              value={f.business}
            />
            <Item
              icon={Mail}
              label={t("about.overview.labels.email")}
              value={
                <a
                  className="text-blue-700 underline underline-offset-2"
                  href={`mailto:${f.email}`}
                >
                  {f.email}
                </a>
              }
            />
            {/* <Item
              icon={Phone}
              label={t("about.overview.labels.phone")}
              value={<a className="text-blue-700" href={`tel:${f.phoneRaw || f.phone}`}>{f.phone}</a>}
            /> */}
            <Item
              icon={MapPin}
              label={t("about.overview.labels.address")}
              value={
                <div className="space-y-1">
                  {f.addressLine1 && <div>{f.addressLine1}</div>}
                  {f.addressLine2 && <div>{f.addressLine2}</div>}
                  {/* {mapHref && (
                    <a
                      className="text-blue-700 underline underline-offset-2"
                      href={mapHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("about.overview.labels.viewMap")}
                    </a>
                  )} */}
                </div>
              }
            />
          </dl>
        </div>

        {/* 오른쪽: 헤더 이미지(회사/건물/오피스 사진) */}
        <div className="order-first md:order-none">
          <img
            src="/images/about/building.jpg"
            alt=""
            className="h-64 w-full rounded-2xl border border-slate-200 object-cover shadow-sm md:h-[360px]"
            loading="lazy"
            decoding="async"
          />
        </div>
      </Container>
    </section>
  );
}

function Item({ icon: Icon, label, value }) {
  return (
    <div className="grid grid-cols-[22px,140px,1fr] items-start gap-3">
      <Icon className="mt-[2px] h-4 w-4 text-slate-500" />
      <dt className="font-semibold text-slate-900">{label}</dt>
      <dd className="min-w-0 break-words">{value || "-"}</dd>
    </div>
  );
}

/* 3) Solutions (핵심 솔루션) — Home.js에서 이동 */
function Solutions() {
  const { t } = useTranslation();
  const cards = t("home.solutions.cards", { returnObjects: true }) || [];

  return (
    <section id="solutions" className="py-16">
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold md:text-3xl">
            {t("home.solutions.title")}
          </h2>
          <p className="mt-2 text-slate-600">{t("home.solutions.desc")}</p>
        </div>

        <div className="mx-auto mt-8 grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-3">
          {cards.map((c) => (
            <div
              key={c.h}
              className="rounded-2xl border bg-white p-6 shadow-sm"
            >
              <div className="text-lg font-semibold">{c.h}</div>
              <p className="mt-1 text-sm text-slate-600">{c.d}</p>

              <ul className="mt-4 space-y-2 text-sm text-slate-700">
                {Array.isArray(c.points) &&
                  c.points.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                      <span>{p}</span>
                    </li>
                  ))}
              </ul>

              {/* {c.href && c.cta && (
                <a
                  href={c.href}
                  className="mt-4 inline-flex text-sm font-semibold text-blue-700 hover:underline"
                >
                  {c.cta}
                </a>
              )} */}
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* 4) How it works (운영 프로세스) — Home.js에서 이동 */
function HowItWorks() {
  const { t } = useTranslation();
  const steps = t("home.how.steps", { returnObjects: true }) || [];

  return (
    <section id="how-it-works" className="py-16">
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold md:text-3xl">
            {t("home.how.title")}
          </h2>
          <p className="mt-2 text-slate-600">{t("home.how.desc")}</p>
        </div>

        <ol className="mx-auto mt-8 grid max-w-5xl grid-cols-1 gap-4 md:grid-cols-6">
          {steps.map((s, i) => (
            <li
              key={`${s}-${i}`}
              className="rounded-2xl border bg-white p-4 text-sm shadow-sm"
            >
              <span className="mb-2 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700">
                STEP {i + 1}
              </span>
              <div className="mt-1 font-medium">{s}</div>
            </li>
          ))}
        </ol>
      </Container>
    </section>
  );
}
